import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { getHardwareOverview } from '../../api/glados';
import {
    selectCurrentChunk,
    selectSelectedFacets,
} from './slice';
import { CHUNK_SIZE } from '../../app/constants';

const createAppSelector = createSelector.withTypes<RootState>();

/**
 * Returns the HardwareOverview query selector
 */
const createHardwareOverviewSelector = createAppSelector(
    [
        state => state,
    ],
    _state => getHardwareOverview.select(),
);

/**
 * Returns the HardwareOverview query
 */
const selectHardwareOverviewQuery = createAppSelector(
    [
        state => state,
        createHardwareOverviewSelector,
    ],
    (state, selector) => selector(state),
);

/**
 * Returns the isLoading state of the HardwareOverview query
 */
const selectIsLoading = createAppSelector(
    [
        selectHardwareOverviewQuery,
    ],
    hardwareOverviewQuery => hardwareOverviewQuery.isLoading,
);

/**
 * Returns the availableFacets
 */
const selectAvailableFacets = createAppSelector(
    [
        selectHardwareOverviewQuery,
    ],
    query => query.data?.categories || [],
);

/**
 * Returns all available devices
 */
const selectDevices = createAppSelector(
    [
        selectHardwareOverviewQuery,
    ],
    query => query.data?.devices || [],
);

/**
 * Returns the devices filtered by the selected facets
 */
const selectFilteredDevices = createAppSelector(
    [
        selectDevices,
        selectSelectedFacets,
    ],
    (devices, selectedFacets) => {
        if (selectedFacets.length === 0) {
            return devices;
        }

        // select those devices where at least one of the device facets matches one of the selected facet
        return devices.filter(device =>
            selectedFacets.some(selectedFacet =>
                device.categories.some(facet => facet.label === selectedFacet.label),
            ),
        );
    },
);

/**
 * Returns the number of filtered devices
 */
const selectNumberOfDevices = createAppSelector(
    [
        selectFilteredDevices,
    ],
    devices => devices.length,
);

/**
 * Returns the devices (filtered and chunked)
 */
const selectDevicesToShow = createAppSelector(
    [
        selectFilteredDevices,
        selectCurrentChunk,
    ],
    (devices, currentChunk) => devices.slice(0, CHUNK_SIZE * currentChunk),
);

/**
 * Returns the number of filtered and chunked devices
 */
const selectNumberOfDevicesToShow = createAppSelector(
    [
        selectDevicesToShow,
    ],
    devices => devices.length,
);

/**
 * Returns a boolean indicating if the currentChunk is the last chunk
 */
const selectIsLastChunk = createAppSelector(
    [
        selectCurrentChunk,
        selectFilteredDevices,
    ],
    (currentChunk, devices) => (currentChunk * CHUNK_SIZE) >= devices.length,
);

export {
    selectIsLoading,
    selectAvailableFacets,
    selectDevices,
    selectNumberOfDevices,
    selectDevicesToShow,
    selectNumberOfDevicesToShow,
    selectIsLastChunk,
};
